import { useEffect, useState } from 'react';
import { DependencyList } from 'react';

import { backendApiInstance } from '@/services/http';
import { Fn, HttpError } from '@/types/common';
import { API_ERROR_STATUS } from '@/utils/restClientBuilder/constants';
import { isFailedReq } from '@/utils/restClientBuilder/helpers';

type UseQueryParams<TArgs, TData> = {
  query: string;
  args?: TArgs;
  deps?: DependencyList;
  skipReq?: boolean;
  processingResponse?: Fn<TData, unknown>;
  customInstance?: typeof backendApiInstance;
};

type UseQueryResult<TData> = {
  isLoading: boolean;
  response: TData | null;
  error: HttpError | null;
  refetch: () => Promise<void>;
};

export const useQuery = <TData, TArgs = never>({
  query,
  args,
  deps = [],
  skipReq = false,
  processingResponse,
  customInstance,
}: UseQueryParams<TArgs, TData>): UseQueryResult<TData> => {
  const [isLoading, setIsLoading] = useState(false);
  const [response, setResponse] = useState<TData | null>(null);
  const [error, setError] = useState<HttpError | null>(null);

  const fetchData = async (signal?: AbortSignal) => {
    setIsLoading(true);
    try {
      const getReqInstance = () => {
        if (customInstance) return customInstance;
        return backendApiInstance;
      };

      const requestInstance = getReqInstance();

      const response = await requestInstance[args ? 'post' : 'get']<TData>({
        url: query,
        body: args ? JSON.stringify(args) : undefined,
        signal,
      });
      const result = response.data;

      if (isFailedReq(response.status, API_ERROR_STATUS)) {
        throw {
          status: response.status,
          message: `HTTP error: ${result}`,
        };
      }

      processingResponse?.(result);

      setResponse(result);
      setError(null);
    } catch (err) {
      if (signal?.aborted) return;

      const error = err as HttpError;
      setError({
        status: error.status || 500,
        message: error.message || 'Unknown error occurred',
      });
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (skipReq) return;
    const controller = new AbortController();
    fetchData(controller.signal);

    return () => controller.abort();
  }, [...deps, skipReq]);

  const refetch = async () => {
    return await fetchData();
  };

  return { isLoading, response, error, refetch };
};
